import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import { AdminHeader } from "../components/AdminNav";
import { MediaPicker } from "../components/MediaPicker";
import { adminMe, adminUpdateSettings, fetchSettings } from "../lib/api";

const blank = {
    eyebrow: "The Studio",
    heading_line_1: "",
    heading_italic: "",
    heading_line_2: "",
    body: "",
    button_label: "More about ASC",
    button_link: "/about",
    image: "",
    image_focal: null,
};

export default function AdminStudio() {
    const [settings, setSettings] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    useEffect(() => {
        fetchSettings()
            .then((s) => {
                setSettings(s);
                setForm({ ...blank, ...(s?.studio_section || {}) });
            })
            .catch((err) => setError(err?.response?.data?.detail || "Load failed"));
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            // Send only the SettingsIn shape — reuse existing endpoint.
            const payload = {
                hero_image: settings?.hero_image || "",
                og_image: settings?.og_image || "",
                favicon: settings?.favicon || "",
                categories: settings?.categories || [],
                project_categories: settings?.project_categories || [],
                project_services: settings?.project_services || [],
                article_categories: settings?.article_categories || [],
                home_services_subheading: settings?.home_services_subheading || "",
                services_page: settings?.services_page || null,
                studio_section: form,
            };
            const saved = await adminUpdateSettings(payload);
            setSettings(saved);
            setSavedAt(new Date());
        } catch (err) {
            setError(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!form) {
        return (
            <main className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16">
                <p className="asc-overline">Loading…</p>
                {error && <p className="text-red-700 text-sm mt-4">{error}</p>}
            </main>
        );
    }

    return (
        <main
            data-testid="page-admin-studio"
            className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24"
        >
            <Seo path="/admin/studio" title="Studio — Admin" description="Manage the Home page Studio section" noindex />
            <AdminHeader title="Studio" />

            {error && <p className="mt-6 text-red-700 text-sm" data-testid="studio-error">{error}</p>}
            {savedAt && <p className="mt-6 text-green-700 text-sm" data-testid="studio-saved">Saved at {savedAt.toLocaleTimeString()}.</p>}

            <p className="mt-8 text-sm text-[#5C5855] max-w-2xl">
                This edits the &quot;Studio&quot; section on the homepage — image on the left, editorial copy and call-to-action on the right. Changes are live immediately.
            </p>

            <form onSubmit={save} className="mt-10 max-w-2xl space-y-6">
                <MediaPicker
                    label="Studio image"
                    testid="studio-image"
                    value={form.image}
                    onChange={(url) => set("image", url)}
                    focal={form.image_focal}
                    onFocalChange={(focal) => set("image_focal", focal)}
                />

                <Field label="Eyebrow / section label">
                    <input
                        data-testid="studio-eyebrow"
                        value={form.eyebrow}
                        onChange={(e) => set("eyebrow", e.target.value)}
                        className={inputCls}
                    />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Heading — line 1">
                        <input data-testid="studio-heading-1" value={form.heading_line_1} onChange={(e) => set("heading_line_1", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Heading — italic word" hint="Rendered italic between line 1 and line 2">
                        <input data-testid="studio-heading-italic" value={form.heading_italic} onChange={(e) => set("heading_italic", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Heading — line 2 (optional)">
                        <input data-testid="studio-heading-2" value={form.heading_line_2} onChange={(e) => set("heading_line_2", e.target.value)} className={inputCls} />
                    </Field>
                </div>
                <Field label="Body content">
                    <textarea
                        data-testid="studio-body"
                        rows={7}
                        value={form.body}
                        onChange={(e) => set("body", e.target.value)}
                        className={inputCls}
                    />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Button text">
                        <input
                            data-testid="studio-button-label"
                            value={form.button_label}
                            onChange={(e) => set("button_label", e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Button link" hint="Internal path (e.g. /about) or full URL">
                        <input
                            data-testid="studio-button-link"
                            value={form.button_link}
                            onChange={(e) => set("button_link", e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                </div>

                <div className="flex items-center gap-6 pt-6 border-t border-[#E5E1DB]">
                    <button
                        type="submit"
                        data-testid="studio-save"
                        disabled={saving}
                        className="px-8 py-3 bg-[#1A1A1A] text-[#F9F8F6] text-[11px] uppercase tracking-[0.22em] hover:bg-black disabled:opacity-50"
                    >
                        {saving ? "Saving…" : "Save Studio section"}
                    </button>
                </div>
            </form>
        </main>
    );
}

const inputCls = "w-full bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm text-[#1A1A1A]";

const Field = ({ label, hint, children }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] mb-2">
            {label}
            {hint && <span className="ml-2 lowercase tracking-normal text-[#A89F94] normal-case">— {hint}</span>}
        </span>
        {children}
    </label>
);
